import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  Input,
  Button,
} from '@aaimplatform/ui';

export function LoginPage() {
  const { login, submitTotp, loginWithGoogle, isAuthenticated, oauthLoading, oauthError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Credential+TOTP second step (ADR-008/009): after a password login returns
  // totpRequired, the advisor enters a 6-digit authenticator code to complete the
  // challenge and receive the mfaVerified access token the connector surface needs.
  const [totpStep, setTotpStep] = useState(false);
  const [totpCode, setTotpCode] = useState('');

  // Pick up error from URL query params (OAuth error redirect)
  const urlError = new URLSearchParams(window.location.search).get('error');
  const urlMessage = new URLSearchParams(window.location.search).get('message');
  const displayError = error ?? oauthError ?? (urlError ? (urlMessage ?? 'Authentication failed') : null);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await login(email, password);
      if (result.totpRequired) {
        setTotpStep(true);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Login failed. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTotpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      await submitTotp(totpCode.trim());
      // On success the provider sets the user; the isAuthenticated redirect above fires.
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Verification failed. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDemoLogin = () => {
    login('advisor@demo-cu.org', 'demo').catch(() => {});
  };

  return (
    <div className="min-h-screen bg-canvas flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-surface rounded shadow-sm border border-recessed overflow-hidden">
          <div className="h-1 bg-accent" />
          <div className="p-8">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img
                src="https://cdn.aaim.app/logos/aaim-master-grey.svg"
                alt="Aaim"
                className="h-8"
              />
            </div>

            {/* Header */}
            <h1 className="text-2xl font-sans font-semibold text-primary text-center mb-1 tracking-tight">
              Advisor Tools
            </h1>
            <p className="text-sm font-sans text-tertiary text-center mb-8">
              Securities-Based Lending Solutions
            </p>

            {/* Error */}
            {displayError && (
              <div className="mb-6 p-3 bg-error/10 border border-error/20 rounded text-error text-sm">
                {displayError}
              </div>
            )}

            {/* Credential+TOTP second step */}
            {totpStep && (
              <form onSubmit={handleTotpSubmit}>
                <p className="text-sm font-sans text-secondary mb-4">
                  Enter the 6-digit code from your authenticator app to finish signing in.
                </p>
                <div className="mb-6">
                  <Input
                    label="Authentication code"
                    id="totp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    placeholder="123456"
                    required
                  />
                </div>
                <Button type="submit" variant="primary" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Verifying...' : 'Verify and sign in'}
                </Button>
              </form>
            )}

            {!totpStep && (
            <>
            {/* Login form */}
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <Input
                  label="Email address"
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="advisor@institution.com"
                  autoComplete="email"
                  required
                />
              </div>

              <div className="mb-6">
                <label
                  htmlFor="password"
                  className="block text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-2"
                >
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-tertiary hover:text-secondary transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>

            {/* OAuth divider */}
            <div className="flex items-center my-6">
              <div className="flex-1 border-t border-recessed" />
              <span className="px-4 text-quaternary text-label-xs font-semibold uppercase">
                Or continue with
              </span>
              <div className="flex-1 border-t border-recessed" />
            </div>

            {/* OAuth buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => { loginWithGoogle().catch(() => {}); }}
                disabled={oauthLoading}
                className="flex items-center justify-center gap-2 px-4 py-2.5 border border-recessed rounded bg-surface hover:bg-elevated transition-colors text-sm font-medium text-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {oauthLoading ? (
                  <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    {/* design-token-skip: Google brand mark (exact brand fills required) */}
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                    {/* design-token-skip: Google brand mark */}
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    {/* design-token-skip: Google brand mark */}
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.42 3.44 1.18 4.93l3.66-2.84z" />
                    {/* design-token-skip: Google brand mark */}
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                )}
                {oauthLoading ? 'Redirecting...' : 'Google'}
              </button>
              <button
                type="button"
                disabled
                className="flex items-center justify-center gap-2 px-4 py-2.5 border border-recessed rounded bg-surface text-sm font-medium text-quaternary cursor-not-allowed"
                title="Microsoft SSO coming soon"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z" />
                </svg>
                Microsoft
              </button>
            </div>
            </>
            )}

            {/* Security footer */}
            <div className="flex items-center justify-center gap-1.5 mt-6 pt-4 border-t border-recessed">
              <Lock size={12} className="text-quaternary" />
              <span className="text-caption-sm text-quaternary">
                Secured with 256-bit encryption
              </span>
            </div>
          </div>
        </div>

        {/* Dev-only demo login */}
        {import.meta.env.DEV && (
          <div className="mt-6">
            <div className="flex items-center my-3">
              <div className="flex-1 border-t border-dashed border-recessed" />
              <span className="px-3 text-quaternary text-label-xs font-semibold uppercase">
                Development
              </span>
              <div className="flex-1 border-t border-dashed border-recessed" />
            </div>
            <button
              type="button"
              className="w-full py-3 px-6 rounded-sm font-sans font-semibold text-sm bg-secondary text-white hover:bg-secondary/90 transition-colors"
              onClick={handleDemoLogin}
            >
              Demo Login (Advisor)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
